import { useTokenUsage } from '../hooks';

/**
 * TokenUsageCard - Card de consumo de tokens
 *
 * Exibe gráfico circular, porcentagem e legenda de uso de tokens
 * Busca dados em tempo real do banco de dados via API
 * Migrado para Tailwind CSS v4
 */
export const TokenUsageCard = () => {
  // Fetch real-time token usage data from API
  const { tokenData } = useTokenUsage();

  const tokensUsed = tokenData?.tokenUsed || 0;
  const tokenLimit = tokenData?.tokenLimit;
  const tokensAvailable = tokenData?.tokensAvailable;
  const usagePercentage = tokenData?.usagePercentage || 0;

  const isUnlimited = tokenLimit === null;

  // Determinar status de saúde
  const getHealthStatus = () => {
    if (isUnlimited) return { label: 'Ilimitado', color: '#10b981' };
    if (usagePercentage < 50) return { label: 'Saudável', color: '#10b981' };
    if (usagePercentage < 80) return { label: 'Atenção', color: '#f59e0b' };
    return { label: 'Crítico', color: '#ef4444' };
  };

  const healthStatus = getHealthStatus();

  // SVG Circle parameters
  const size = 120;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (usagePercentage / 100) * circumference;

  return (
    <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] hover:border-[#F4CC04] transition-all duration-400">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm font-medium text-[#CCCCCC] m-0">Consumo de Tokens</div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#1a1a1a] rounded-md">
          <svg className="w-3.5 h-3.5" fill="currentColor" style={{ color: healthStatus.color }} viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span className="text-xs font-semibold" style={{ color: healthStatus.color }}>
            {healthStatus.label}
          </span>
        </div>
      </div>

      {/* Main Value */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2 m-0">
          <span className="text-3xl font-bold text-white m-0">
            {tokensUsed.toLocaleString('pt-BR')}
          </span>
          <span className="text-lg text-[#666666] m-0">
            / {isUnlimited ? '∞' : (tokenLimit ?? 0).toLocaleString('pt-BR')}
          </span>
        </div>
      </div>

      {/* Circular Progress and Legend */}
      <div className="flex items-center gap-6">
        {/* Circular Progress */}
        {!isUnlimited && (
          <div className="relative flex-shrink-0">
            <svg width={size} height={size} className="transform -rotate-90">
              {/* Background circle */}
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="#1a1a1a"
                strokeWidth={strokeWidth}
              />
              {/* Progress circle */}
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="#F4CC04"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
            </svg>
            {/* Percentage */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-[#F4CC04]">{usagePercentage}%</span>
              <span className="text-xs text-[#666666]">utilizado</span>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#F4CC04]"></div>
              <span className="text-xs text-[#CCCCCC]">Tokens Utilizados</span>
            </div>
            <span className="text-sm font-semibold text-white tabular-nums">
              {tokensUsed.toLocaleString('pt-BR')}
            </span>
          </div>

          {!isUnlimited && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#4a4a4a]"></div>
                <span className="text-xs text-[#CCCCCC]">Tokens Disponíveis</span>
              </div>
              <span className="text-sm font-semibold text-white tabular-nums">
                {(tokensAvailable ?? 0).toLocaleString('pt-BR')}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#F4CC04]"></div>
              <span className="text-xs text-[#CCCCCC]">Limite Total</span>
            </div>
            <span className="text-sm font-semibold text-white tabular-nums">
              {isUnlimited ? '∞' : (tokenLimit ?? 0).toLocaleString('pt-BR')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
